import React from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Play, Edit3, Clock, Presentation } from 'lucide-react'
import { calculateTotalDuration, getSlideTypeDescription, validateVisualElements } from './pptUtils'
import LoadingSkeleton from '../../components/LoadingSkeleton'

const PPTOutlinePage = ({ query, outline, onStartGeneration, onBack, isLoading, error }) => {
  const totalDuration = calculateTotalDuration(outline)
  const slideCount = outline.length

  if (isLoading && outline.length === 0) {
    return (
      <div className="min-h-screen bg-bg-primary p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={onBack}
                className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-text-secondary" />
              </button>
              <div className="flex items-center gap-2">
                <Presentation className="w-6 h-6 text-purple-500" />
                <h1 className="text-2xl font-bold text-text-primary">PPT Agent</h1>
              </div>
            </div>
            
            <LoadingSkeleton 
              message="Sedang membuat outline presentasi..."
              submessage="Menganalisis topik dan menyusun struktur slide"
            />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-primary p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={onBack}
                className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-text-secondary" />
              </button>
              <div className="flex items-center gap-2">
                <Presentation className="w-6 h-6 text-purple-500" />
                <h1 className="text-2xl font-bold text-text-primary">PPT Agent</h1>
              </div>
            </div>
            
            <div className="text-center py-8">
              <div className="text-red-400 text-lg font-medium mb-2">Terjadi Kesalahan</div>
              <div className="text-text-secondary mb-4">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Coba Lagi
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-primary p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={onBack}
              className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-secondary" />
            </button>
            <div className="flex items-center gap-2">
              <Presentation className="w-6 h-6 text-purple-500" />
              <h1 className="text-2xl font-bold text-text-primary">PPT Agent</h1>
            </div>
          </div>

          {/* Query Display */}
          <div className="bg-bg-tertiary rounded-lg p-4 mb-6 border border-border-secondary">
            <h2 className="text-lg font-semibold text-text-primary mb-2">Topik Presentasi</h2>
            <p className="text-text-secondary">{query}</p>
          </div>

          {/* Presentation Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
              <div className="flex items-center gap-2">
                <Presentation className="w-5 h-5 text-blue-400" />
                <span className="text-text-primary font-medium">Total Slide</span>
              </div>
              <div className="text-2xl font-bold text-text-primary mt-1">{slideCount}</div>
            </div>
            
            <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-400" />
                <span className="text-text-primary font-medium">Durasi Estimasi</span>
              </div>
              <div className="text-2xl font-bold text-text-primary mt-1">{totalDuration} menit</div>
            </div>
            
            <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
              <div className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-purple-400" />
                <span className="text-text-primary font-medium">Status</span>
              </div>
              <div className="text-lg font-bold text-text-primary mt-1">Siap Generate</div>
            </div>
          </div>

          {/* Outline Editor */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-text-primary">Struktur Presentasi</h3>
            </div>

            <div className="space-y-3">
              {outline.map((slide, index) => (
                <div key={slide.id} className="border border-border-secondary rounded-lg p-4 hover:border-purple-400 transition-colors bg-bg-tertiary">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-sm font-medium">
                          Slide {index + 1}
                        </span>
                        <span className="bg-bg-primary text-text-secondary px-2 py-1 rounded text-sm border border-border-primary">
                          {slide.slideType}
                        </span>
                        <span className="text-text-secondary text-sm flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {slide.duration} menit
                        </span>
                      </div>
                      
                      <h4 className="font-semibold text-text-primary mb-1">{slide.title}</h4>
                      <p className="text-text-secondary text-sm mb-2">{slide.description}</p>
                      
                      <div className="flex flex-wrap gap-1">
                        {slide.visualElements?.map((element, idx) => (
                          <span key={idx} className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">
                            {element}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 ml-4">
                      <span className="text-xs text-text-secondary bg-bg-primary px-2 py-1 rounded border border-border-secondary">
                        Read-only
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              disabled={isLoading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Regenerate Outline
            </button>
            <button
              onClick={() => onStartGeneration(outline)}
              disabled={isLoading || outline.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Play className="w-5 h-5" />
              {isLoading ? 'Sedang Memproses...' : 'Mulai Generate Presentasi'}
            </button>
          </div>
        </div>
      </div>


    </div>
  )
}

PPTOutlinePage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  onStartGeneration: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string
}

export default PPTOutlinePage